import { InputAdornment, MenuItem, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import EmployeePageButtons from "../employeePageButtons/employeePageButtons";
import "./employeeAttachedDocs.css";
import SearchIcon from "@mui/icons-material/Search";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useParams } from "react-router-dom";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "@mui/material/Modal";
import CancelIcon from "@mui/icons-material/Cancel";
import StyledSwitch from "@/components/common/baseSwitch";
import { CacheProvider } from "@emotion/react";
import authStore from "@/stores/authStore";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import {
    AddDocRequest,
    AttachedDoc,
    EmployeeDocsFilterRequest,
    EmployeeDocsFilterResponse,
    IdText,
    PermissionName,
} from "@/models/apiModels";
import { DocCategory } from "@/models/enums";
import popupService from "@/services/popupService";
import api from "@/services/api";
import dayjs from "dayjs";
import BaseInput from "@/components/common/baseInput";
import BaseTooltip from "@/components/common/baseTooltip";
import BaseDateInput from "@/components/common/baseDateInput";
import BaseButton from "@/components/common/baseButton";
import { checkEditPermissionWithoutAlert, checkPermissionsWithAlert, formatDate } from "@/util";
import filtersStore from "@/stores/filtersStore";
import TotalCountComponent from "@/components/common/resultCountComponent";
import ClearIcon from "@/assets/icons/clear-icon.svg";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const EmployeeAttachedDocs = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [docToAdd, setDocToAdd] = useState<AddDocRequest>({
        employeeId: Number(id),
        document: null,
        name: "",
        documentName: "",
        category: null,
        isActive: true,
    });
    const [employeeDocFilter, setEmployeeDocFilter] = useState<EmployeeDocsFilterRequest>({
        keywords: "",
        fromDate: null,
        tillDate: null,
        docStatus: "",
        category: null,
        employeeId: Number(id),
        orderBy: "",
        asc: false,
        pageIndex: 0,
    });
    const [hasNext, setHasNext] = useState(true);
    const [attachedDocs, setAttachedDocs] = useState<AttachedDoc[]>([]);
    const [selectedDoc, setSelectedDoc] = useState<AttachedDoc>({
        id: 0,
        createdAt: "",
        attachedBy: "",
        category: null,
        name: "",
        documentName: "",
        isActive: false,
    });
    const inputFile = useRef<HTMLInputElement>(null);
    const [searchCounter, setSearchCounter] = useState(0);
    const [showEditModal, setShowEditModal] = useState(false);
    const [total, setTotal] = useState(0);
    const [totalFilter, setTotalFilter] = useState(0);

    const docCategoryTypeList: IdText[] = (
        Object.values(DocCategory).filter((val) => isNaN(Number(val))) as (keyof typeof DocCategory)[]
    ).map((key) => new IdText(key, t(`enums.DocCategory.${key}`)));

    const docStatus = [
        {
            value: "Active",
            label: t("employee.ActiveStatus"),
        },
        {
            value: "Inactive",
            label: t("employee.InactiveStatus"),
        },
    ];

    function handleClearFilters() {
        filtersStore.setEmployeeDocFilterData(null);
        setEmployeeDocFilter((prevData) => ({
            ...prevData,
            keywords: "",
            fromDate: null,
            tillDate: null,
            category: null,
            orderBy: "",
            asc: false,
            docStatus: "",
        }));
        setSearchCounter(searchCounter + 1);
    }

    function handleCloseAddModal() {
        setDocToAdd((prevData) => ({
            ...prevData,
            document: null,
            name: "",
            documentName: "",
            category: null,
        }));

        if (inputFile.current) {
            inputFile.current.value = "";
        }
    }

    function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const { files } = e.target;
        if (files && files.length) {
            const filename = files[0].name;
            const doc = files[0];

            setDocToAdd((prevData) => ({
                ...prevData,
                document: doc,
                name: filename,
            }));
        }
    }

    function handleOnChangeFromDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setEmployeeDocFilter((prevData) => ({
            ...prevData,
            fromDate: date.isValid() ? formattedDate : null,
        }));
    }

    function handleOnChangeTillDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setEmployeeDocFilter((prevData) => ({
            ...prevData,
            tillDate: date.isValid() ? formattedDate : null,
        }));
    }

    async function editDocOnClick(event: React.FormEvent) {
        event.preventDefault();
        setShowEditModal(false);
        popupService.showLoading();
        try {
            const request = {
                id: selectedDoc.id,
                name: selectedDoc.name,
                documentName: selectedDoc.documentName,
                category: selectedDoc.category,
                isActive: selectedDoc.isActive,
            };
            await api.editDoc(request);
            await popupService.showSuccess(t("employee.Docs.EditText"));
            onSearchClick();
        } catch (err) {
            popupService.showApiError(err);
            return;
        }
    }

    async function addDocOnClick(event: React.FormEvent) {
        event.preventDefault();
        handleCloseAddModal();
        popupService.showLoading();
        try {
            await api.addDoc(docToAdd);
            await popupService.showSuccess(t("employee.Docs.AddText"));
            onSearchClick();
        } catch (err) {
            popupService.showApiError(err);
            return;
        }
    }

    function onClickSetDoc() {
        if (inputFile.current) {
            inputFile.current.click();
        }
    }

    async function onSearchClick() {
        setAttachedDocs([]);
        popupService.showLoading();
        filtersStore.setEmployeeDocFilterData(null);
        setEmployeeDocFilter((prevData) => ({
            ...prevData,
            pageIndex: 0,
        }));
        setSearchCounter(searchCounter + 1);
    }

    const handleScroll = useCallback(() => {
        const docsContainer = document.getElementById("employee-docs-list-grid");
        const doScroll =
            docsContainer &&
            docsContainer.scrollTop + docsContainer.clientHeight >= docsContainer.scrollHeight &&
            docsContainer.scrollTop > 0;

        if (!doScroll || !hasNext) {
            return;
        }
        filtersStore.setEmployeeDocFilterData(null);
        setEmployeeDocFilter((prevData) => ({
            ...prevData,
            pageIndex: employeeDocFilter.pageIndex + 1,
        }));
    }, [employeeDocFilter.pageIndex, hasNext]);

    async function getEmployeeDocs(): Promise<EmployeeDocsFilterResponse> {
        const filterStore = filtersStore.employeesDocsFilterData;
        if (filterStore != null && filterStore.employeeId === Number(id)) {
            if (filterStore.pageIndex !== 0) {
                filterStore.pageIndex = 0;
            }
            setEmployeeDocFilter(filterStore ?? new EmployeeDocsFilterRequest());
            const responsePromise = api.getEmployeeDocs(filterStore ?? new EmployeeDocsFilterRequest());
            return (await responsePromise).data;
        }

        filtersStore.setEmployeeDocFilterData(employeeDocFilter);
        const responsePromise = api.getEmployeeDocs(employeeDocFilter);
        return (await responsePromise).data;
    }

    let startFetchingBusy = false;
    async function startFetching() {
        if (!checkPermissionsWithAlert(PermissionName.EmployeeAttachedDocs)) {
            return;
        }
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            popupService.showLoading();
            const data = await getEmployeeDocs();
            const apiResponse = data;
            setHasNext(apiResponse.hasNext);
            if (employeeDocFilter.pageIndex === 0) {
                setAttachedDocs(apiResponse.rows);
                setTotal(data.total);
                setTotalFilter(data.totalFilter);
            } else {
                setAttachedDocs((oldArray) => [...oldArray, ...apiResponse.rows]);
            }
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    }

    async function sortBy(sortBy: string) {
        if (employeeDocFilter.orderBy !== sortBy) {
            setEmployeeDocFilter((prevData) => ({
                ...prevData,
                asc: true,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        } else {
            setEmployeeDocFilter((prevData) => ({
                ...prevData,
                asc: !employeeDocFilter.asc,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        }
        setAttachedDocs([]);
        filtersStore.setEmployeeDocFilterData(null);
    }

    function getArrowClass(sort: string) {
        if (sort === employeeDocFilter.orderBy) {
            if (employeeDocFilter.asc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [employeeDocFilter.pageIndex, employeeDocFilter.orderBy, employeeDocFilter.asc, searchCounter]);

    useEffect(() => {
        const docsContainer = document.getElementById("employee-docs-list-grid");
        if (!docsContainer) {
            return;
        }
        docsContainer.addEventListener("scroll", handleScroll);
        return () => {
            docsContainer.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    function handleKeyPressSearch(event: any) {
        if (event.key === "Enter") {
            onSearchClick();
        }
    }

    useEffect(() => {
        document.addEventListener("keypress", handleKeyPressSearch);
        return () => {
            document.removeEventListener("keypress", handleKeyPressSearch);
        };
    });

    const addModal = docToAdd.document ? (
        <div>
            <Modal
                open={!!docToAdd.document}
                onClose={() => handleCloseAddModal()}
                sx={{ backdropFilter: "blur(6px)" }}
            >
                <form onSubmit={(event) => addDocOnClick(event)}>
                    <div className="modal-style">
                        <div className="employee-docs-modal-header">
                            <div style={{ paddingTop: "15px" }} className="font-style-24">
                                {t("employee.Docs.AddNewDoc")}
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                <CancelIcon
                                    onClick={() => handleCloseAddModal()}
                                    style={{ color: "#586A84", cursor: "pointer" }}
                                />
                            </div>
                        </div>
                        <div className="employee-docs-modal-content">
                            <div>
                                <BaseInput
                                    label={t("employee.Docs.documentName")}
                                    type="search"
                                    size="small"
                                    required
                                    value={docToAdd.documentName}
                                    onChange={(event) =>
                                        setDocToAdd((prevData) => ({
                                            ...prevData,
                                            documentName: event.target.value,
                                        }))
                                    }
                                    sx={{
                                        width: "100%",
                                    }}
                                ></BaseInput>
                            </div>
                            <div>
                                <BaseInput
                                    label={t("employee.Docs.Name")}
                                    type="search"
                                    size="small"
                                    value={docToAdd.name}
                                    required
                                    onChange={(event) =>
                                        setDocToAdd((prevData) => ({
                                            ...prevData,
                                            name: event.target.value,
                                        }))
                                    }
                                    sx={{
                                        width: "100%",
                                    }}
                                ></BaseInput>
                            </div>
                            <div>
                                <BaseInput
                                    select
                                    label={t("employee.Docs.Category")}
                                    type="search"
                                    size="small"
                                    required
                                    value={docToAdd.category ?? ""}
                                    onChange={(event: any) =>
                                        setDocToAdd((prevData) => ({
                                            ...prevData,
                                            category: event.target.value,
                                        }))
                                    }
                                    sx={{
                                        width: "100%",
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton
                                                className="select-clear-icon"
                                                sx={{
                                                    visibility: docToAdd.category ? "visible" : "hidden",
                                                }}
                                                onClick={() =>
                                                    setDocToAdd((prevData) => ({
                                                        ...prevData,
                                                        category: null,
                                                    }))
                                                }
                                            >
                                                <img src={ClearIcon} alt="Clear icon" />
                                            </IconButton>
                                        ),
                                    }}
                                >
                                    {docCategoryTypeList.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.text}
                                        </MenuItem>
                                    ))}
                                </BaseInput>
                            </div>
                            <div className="edit-doc-status font-style-16">
                                <span>{t("employee.NewEmpStatus")}</span>
                                <StyledSwitch
                                    value={docToAdd.isActive}
                                    checked={docToAdd.isActive}
                                    onChange={() =>
                                        setDocToAdd((prevData) => ({
                                            ...prevData,
                                            isActive: !docToAdd.isActive,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div
                            className="employee-docs-modal-footer"
                            style={{ display: "flex", justifyContent: "center", gap: "10px" }}
                        >
                            <BaseButton
                                style={{ padding: "0px 42px" }}
                                onClick={() => handleCloseAddModal()}
                                className="cancel-button"
                            >
                                {t("button.cancel")}
                            </BaseButton>
                            <BaseButton type="submit" style={{ padding: "0px 50px" }} className="edit-save-button">
                                {t("button.save")}
                            </BaseButton>
                        </div>
                    </div>
                </form>
            </Modal>
        </div>
    ) : (
        ""
    );

    const editModal = showEditModal ? (
        <div>
            <Modal open={showEditModal} onClose={() => setShowEditModal(false)} sx={{ backdropFilter: "blur(6px)" }}>
                <form onSubmit={(event) => editDocOnClick(event)}>
                    <div className="modal-style">
                        <div className="employee-docs-modal-header">
                            <div style={{ paddingTop: "15px" }} className="font-style-24">
                                {t("employee.Docs.Edit")}
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                <CancelIcon
                                    onClick={() => setShowEditModal(false)}
                                    style={{ color: "#586A84", cursor: "pointer" }}
                                />
                            </div>
                        </div>
                        <div className="employee-docs-modal-content">
                            <div>
                                <BaseInput
                                    label={t("employee.Docs.documentName")}
                                    type="search"
                                    size="small"
                                    required
                                    value={selectedDoc.documentName}
                                    onChange={(event: any) =>
                                        setSelectedDoc((prevData) => ({
                                            ...prevData,
                                            documentName: event.target.value,
                                        }))
                                    }
                                    sx={{
                                        width: "100%",
                                    }}
                                ></BaseInput>
                            </div>
                            <div>
                                <BaseInput
                                    label={t("employee.Docs.Name")}
                                    type="search"
                                    size="small"
                                    value={selectedDoc.name}
                                    onChange={(event: any) =>
                                        setSelectedDoc((prevData) => ({
                                            ...prevData,
                                            name: event.target.value,
                                        }))
                                    }
                                    required
                                    sx={{
                                        width: "100%",
                                    }}
                                    inputProps={{ readOnly: true }}
                                ></BaseInput>
                            </div>
                            <div>
                                <BaseInput
                                    select
                                    label={t("employee.Docs.Category")}
                                    type="search"
                                    size="small"
                                    required
                                    value={selectedDoc.category}
                                    onChange={(event: any) =>
                                        setSelectedDoc((prevData) => ({
                                            ...prevData,
                                            category: event.target.value,
                                        }))
                                    }
                                    sx={{
                                        width: "100%",
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton
                                                className="select-clear-icon"
                                                sx={{
                                                    visibility: selectedDoc.category ? "visible" : "hidden",
                                                }}
                                                onClick={() =>
                                                    setSelectedDoc((prevData) => ({
                                                        ...prevData,
                                                        category: null,
                                                    }))
                                                }
                                            >
                                                <img src={ClearIcon} alt="Clear icon" />
                                            </IconButton>
                                        ),
                                    }}
                                >
                                    {docCategoryTypeList.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.text}
                                        </MenuItem>
                                    ))}
                                </BaseInput>
                            </div>
                            <div className="edit-doc-status font-style-16">
                                <span>{t("employee.NewEmpStatus")}</span>
                                <StyledSwitch
                                    value={selectedDoc.isActive}
                                    checked={selectedDoc.isActive}
                                    onChange={() =>
                                        setSelectedDoc((prevData) => ({
                                            ...prevData,
                                            isActive: !selectedDoc.isActive,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div
                            className="employee-docs-modal-footer"
                            style={{ display: "flex", justifyContent: "center", gap: "10px" }}
                        >
                            <BaseButton
                                style={{ padding: "0px 42px" }}
                                onClick={() => setShowEditModal(false)}
                                className="cancel-button"
                            >
                                {t("button.cancel")}
                            </BaseButton>
                            <BaseButton style={{ padding: "0px 50px" }} className="edit-save-button" type="submit">
                                {t("button.save")}
                            </BaseButton>
                        </div>
                    </div>
                </form>
            </Modal>
        </div>
    ) : (
        ""
    );

    function handleOpenEditModal(doc: AttachedDoc) {
        setSelectedDoc(doc);
        setShowEditModal(true);
    }

    useEffect(() => {
        document.addEventListener("keypress", handleKeyPressSearch);
        return () => {
            document.removeEventListener("keypress", handleKeyPressSearch);
        };
    });

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            <div className="employee-docs-container">
                {addModal}
                {editModal}
                <div className="employee-docs-header">
                    <div>
                        <div className="employee-docs-buttons">
                            <EmployeePageButtons />
                            <div className="employee-docs-add-clear-btn">
                                <input
                                    type="file"
                                    style={{ display: "none" }}
                                    ref={inputFile}
                                    onChange={handleFileUpload}
                                />

                                {checkEditPermissionWithoutAlert(PermissionName.AddAttachedDoc) ? (
                                    <BaseButton
                                        style={{ padding: "0px 30px" }}
                                        className="edit-save-button"
                                        onClick={onClickSetDoc}
                                    >
                                        {t("employee.Docs.AddNewDoc")}
                                    </BaseButton>
                                ) : (
                                    ""
                                )}
                            </div>
                        </div>
                        <div className="employee-docs-filter">
                            <div className="font-style employee-docs-span">{t("employee.FilterHeader")}</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                                <div className="employee-docs-filter-row">
                                    <div>
                                        <BaseInput
                                            className="search-input"
                                            placeholder={`${t("employee.Docs.Search")}`}
                                            type="search"
                                            size="small"
                                            value={employeeDocFilter?.keywords}
                                            onChange={(event) =>
                                                setEmployeeDocFilter((prevData) => ({
                                                    ...prevData,
                                                    keywords: event.target.value,
                                                }))
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                width: "300px",
                                            }}
                                        />
                                    </div>
                                    <div style={{ width: 160 }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DesktopDatePicker
                                                className="date-picker-input"
                                                label={t("employee.Docs.FromDate")}
                                                inputFormat="DD/MM/YYYY"
                                                value={employeeDocFilter?.fromDate}
                                                onChange={(event) => handleOnChangeFromDate(event)}
                                                renderInput={(params) => <BaseDateInput {...params} />}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                    <div style={{ width: 160 }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DesktopDatePicker
                                                className="date-picker-input"
                                                label={t("employee.Docs.TillDate")}
                                                inputFormat="DD/MM/YYYY"
                                                value={employeeDocFilter?.tillDate}
                                                onChange={(event) => handleOnChangeTillDate(event)}
                                                renderInput={(params) => <BaseDateInput {...params} />}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                    <div>
                                        <BaseInput
                                            select
                                            label={t("employee.Docs.Status")}
                                            type="search"
                                            size="small"
                                            value={employeeDocFilter?.docStatus ?? ""}
                                            onChange={(event) =>
                                                setEmployeeDocFilter((prevData) => ({
                                                    ...prevData,
                                                    docStatus: event.target.value,
                                                }))
                                            }
                                            sx={{
                                                width: "200px",
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        className="select-clear-icon"
                                                        sx={{
                                                            visibility: employeeDocFilter?.docStatus
                                                                ? "visible"
                                                                : "hidden",
                                                        }}
                                                        onClick={() =>
                                                            setEmployeeDocFilter((prevData) => ({
                                                                ...prevData,
                                                                docStatus: "",
                                                            }))
                                                        }
                                                    >
                                                        <img src={ClearIcon} alt="Clear icon" />
                                                    </IconButton>
                                                ),
                                            }}
                                        >
                                            {docStatus.map((option) => (
                                                <MenuItem key={option.label} value={option.label}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </BaseInput>
                                    </div>
                                    <div>
                                        <BaseInput
                                            select
                                            label={t("employee.Docs.Category")}
                                            type="search"
                                            size="small"
                                            value={employeeDocFilter?.category ?? ""}
                                            onChange={(event: any) =>
                                                setEmployeeDocFilter((prevData) => ({
                                                    ...prevData,
                                                    category: event.target.value,
                                                }))
                                            }
                                            sx={{
                                                width: "260px",
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        className="select-clear-icon"
                                                        sx={{
                                                            visibility: employeeDocFilter?.category
                                                                ? "visible"
                                                                : "hidden",
                                                        }}
                                                        onClick={() =>
                                                            setEmployeeDocFilter((prevData) => ({
                                                                ...prevData,
                                                                category: null,
                                                            }))
                                                        }
                                                    >
                                                        <img src={ClearIcon} alt="Clear icon" />
                                                    </IconButton>
                                                ),
                                            }}
                                        >
                                            {docCategoryTypeList.map((option) => (
                                                <MenuItem key={option.id} value={option.id}>
                                                    {option.text}
                                                </MenuItem>
                                            ))}
                                        </BaseInput>
                                    </div>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: 15,
                                        marginInlineStart: 10,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <span
                                            onClick={handleClearFilters}
                                            className="link-element clear-all-btn"
                                            style={{
                                                minWidth: "70px",
                                            }}
                                        >
                                            {t("display.ClearAll")}
                                        </span>
                                    </div>
                                    <BaseButton
                                        style={{ padding: "0px 25px" }}
                                        className="basic-button"
                                        onClick={() => onSearchClick()}
                                    >
                                        {t("general.filter")}
                                    </BaseButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <TotalCountComponent
                    title={t("employees.QuantityOfDocuments") as string}
                    allCount={total}
                    currentCount={totalFilter}
                />
                <div className="employee-docs-content">
                    <div id="employee-docs-list-grid" className="employee-docs-list-grid">
                        <div className="employee-docs-fake-thead"></div>
                        <table>
                            <thead>
                                <tr>
                                    <td onClick={() => sortBy("Name")}>
                                        <span>
                                            {t("employee.Docs.documentName")}
                                            <i className={`icon-f ${getArrowClass("Name")} fs-12`} />
                                        </span>
                                    </td>
                                    <td onClick={() => sortBy("AttachmentDate")}>
                                        <div className="table-number-format-name">
                                            <span>
                                                {t("employee.Docs.AttachmentDate")}
                                                <i className={`icon-f ${getArrowClass("AttachmentDate")} fs-12`} />
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-number-format-name">{t("employee.Docs.Number")}</div>
                                    </td>
                                    <td onClick={() => sortBy("AttachedBy")}>
                                        <span>
                                            {t("employee.Docs.AttachedBy")}
                                            <i className={`icon-f ${getArrowClass("AttachedBy")} fs-12`} />
                                        </span>
                                    </td>
                                    <td onClick={() => sortBy("Category")}>
                                        <span>
                                            {t("employee.Docs.Category")}
                                            <i className={`icon-f ${getArrowClass("Category")} fs-12`} />
                                        </span>
                                    </td>
                                    <td>{t("employee.Docs.Name")}</td>
                                    <td>{t("employee.Docs.Status")}</td>
                                    <td></td>
                                </tr>
                            </thead>
                            <tbody>
                                {attachedDocs.map((doc, index) => (
                                    <tr key={index}>
                                        <td>{doc.documentName}</td>
                                        <td>
                                            <div className="table-number-format-value">{formatDate(doc.createdAt)}</div>
                                        </td>
                                        <td>
                                            <div className="table-number-format-value">{doc.id}</div>
                                        </td>
                                        <td>{doc.attachedBy}</td>
                                        <td>{t(`enums.DocCategory.${doc.category}`)}</td>
                                        <td>
                                            <a
                                                href={api.generateEmployeeDocLink(doc.id)}
                                                className="link-element"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {doc.name}
                                            </a>
                                        </td>

                                        <td>
                                            {doc.isActive ? (
                                                <div className="status-element">
                                                    <div className="status-color-active"></div>
                                                    <div>{t("employee.ActiveStatus")}</div>
                                                </div>
                                            ) : (
                                                <div className="status-element">
                                                    <div className="status-color-inactive"></div>
                                                    <div>{t("employee.InactiveStatus")}</div>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <BaseTooltip title={t("button.edit")}>
                                                <i
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => handleOpenEditModal(doc)}
                                                    className={"icon-f icon-PencilIcon fs-20"}
                                                />
                                            </BaseTooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </CacheProvider>
    );
};

export default EmployeeAttachedDocs;
